import React, { memo, useRef } from "react";
import { Box, FormControl, FormHelperText, InputLabel } from "@mui/material";
import { ipdLabelSx } from "./ipdFieldStyles";

// Ids have to be unique per mounted field, not derived from the label: the
// same label is reused on one screen (OtMasters renders "Operating Suite"
// three times), and duplicate ids would point every one of those labels at
// whichever input the browser found first.
let seq = 0;

function IpdFormField({
  label,
  children,
  error,
  helperText,
  className,
  required,
  id,
  sx,
}) {
  const generated = useRef(null);
  if (generated.current === null) generated.current = `ipd-field-${(seq += 1)}`;
  const fieldId = id || generated.current;
  const labelId = `${fieldId}-label`;
  const message = error || helperText;

  // Give the label something it can actually label.
  //
  // `htmlFor` only binds to a labelable element — input, select, textarea,
  // button. It used to point at the wrapper <div>, which is none of those, so
  // the binding silently did nothing and every control in the app went
  // unnamed to a screen reader while looking perfectly labelled on screen.
  //
  // A single component child (IpdTextField, OtSelect, MUI Select…) gets the
  // id, because MUI forwards `id` down to the input it renders. A single
  // intrinsic child (<div> of chips, a radio row) cannot take one usefully,
  // so that case is named as a group instead — several controls under one
  // label is what a group is for.
  const only =
    React.Children.count(children) === 1 ? React.Children.only(children) : null;
  const bindable =
    React.isValidElement(only) && typeof only.type !== "string" ? only : null;
  const boundChild = bindable
    ? React.cloneElement(bindable, { id: bindable.props.id || fieldId })
    : null;

  return (
    <FormControl
      fullWidth
      size="small"
      error={Boolean(error)}
      className={className}
      sx={{ mb: 0, ...sx }}
    >
      {label ? (
        <InputLabel
          shrink
          id={labelId}
          htmlFor={fieldId}
          required={required}
          sx={ipdLabelSx}
        >
          {label}
        </InputLabel>
      ) : null}
      {/* The Box stays even when it carries nothing: 297 call sites lay out
          against it, and removing the div would reflow them. */}
      <Box
        {...(boundChild || !label
          ? {}
          : { role: "group", "aria-labelledby": labelId })}
      >
        {boundChild || children}
      </Box>
      <FormHelperText
        role={error ? "alert" : undefined}
        sx={{
          minHeight: 20,
          lineHeight: "20px",
          mx: 0,
          mt: 0.25,
        }}
      >
        {message || " "}
      </FormHelperText>
    </FormControl>
  );
}

export default memo(IpdFormField);
